import homepageQueries from '../gql/queries';
import { fetchFromGraphql } from 'entities/helpers/gqlCall';
import { getDateString } from 'base/new-utils';
import { getUserContactIdForOrg } from 'apps/tasks/utils/contact-helpers';

const EMPTY_ACTION_TYPE_COUNTS = {
  CALL: 0,
  EMAIL: 0,
  LINKEDIN: 0,
  RESEARCH: 0,
  PRINT_MAIL: 0,
  TEXT: 0,
  THANK_VIEW: 0,
  OTHER: 0,
  TOTAL: 0,
};

// Fetches the current user's own tasks to split into cadence steps vs non-cadence tasks.
// Cadence 1.0 = journey_task_id set; Cadence 2.0 = task_source === 'cadence_v2'.
// The legacy todayActiveTaskCount API has too broad a scope and doesn't distinguish these
// correctly, so when the signal flag is on we bypass it for the home-page count display.
const userTaskSplitQuery = String.raw`
  query ($owner_user_id: BigInt, $statuses: [String], $limit: Int) {
    tasks(owner_user_id: $owner_user_id, statuses: $statuses, limit: $limit) {
      items {
        action_type
        current_due_date
        task_source
        cadence_id
        journey_task_id
      }
    }
  }
`;

//Common function to fetch counts for today/next five days for Journey and Other Tasks based on day range field.
// dayRange 1 : Today's Count
// dayRange 7 : Next Five Days Count
// fetchJourneysTaskCount: If TRUE - Fetches journey counts, if FALSE fetches other task count

//Fetches counts for today/next five days for Journey and Other Tasks based on day range field.
export const getUpcomingTaskCounts = async (
  taskOwnerUserId,
  taskStatuses,
  includeInactive,
  fetchJourneysTaskCount,
  dayRange
) => {
  const query = homepageQueries.fetchUpcomingTaskCounts;
  const queryVars = {
    taskOwnerUserId,
    taskStatuses,
    includeInactive,
    fetchJourneysTaskCount,
    dayRange,
    today: getDateString(),
  };

  const { getUpcomingTaskCounts } = await fetchFromGraphql(query, queryVars, 'getUpcomingTaskCounts');
  return getUpcomingTaskCounts;
};

/**
 *
 *
 * @returns array with two objects: one for "other" tasks (not in a journey) and one for journey tasks
 * [ { CALL: 0, EMAIL: 1 ... etc}, { CALL : 0, EMAIL: 1 ... etc} ]
 */
export const getTaskCountForToday = async () => {
  const query = homepageQueries.activeTaskCountForToday;

  const { todayActiveTaskCount } = await fetchFromGraphql(
    query,
    { selectedDate: getDateString() },
    'todayActiveTaskCount'
  );
  return todayActiveTaskCount;
};

/**
 * When the `signal` flag is on, bypass the legacy todayActiveTaskCount API (which has
 * org-wide scope and can't distinguish cadence 1.0 vs 2.0 vs non-cadence) and instead
 * fetch the user's own tasks client-side. Returns [otherCounts, cadenceStepsCounts]
 * matching the array shape expected by today-content.js.
 *
 * Cadence steps = journey_task_id set (1.0) OR task_source === 'cadence_v2' (2.0).
 * Non-cadence = everything else.
 * Both buckets are filtered to current_due_date <= today (overdue + due today).
 *
 * @param {number} userId
 * @returns {Array|null} [otherCounts, cadenceStepsCounts] or null on error
 */
export const getUserTaskCountsForToday = async (userId) => {
  if (!userId) return null;
  try {
    const today = getDateString();
    const resp = await fetchFromGraphql(
      userTaskSplitQuery,
      { owner_user_id: userId, statuses: ['IN_PROGRESS'], limit: 10000 },
      'tasks'
    );
    const items = (resp?.tasks?.items || []).filter((t) => t.current_due_date <= today);
    const cadenceCounts = { ...EMPTY_ACTION_TYPE_COUNTS };
    const otherCounts = { ...EMPTY_ACTION_TYPE_COUNTS };
    items.forEach((task) => {
      const isCadenceStep = task.journey_task_id || (task.task_source === 'cadence_v2' && task.cadence_id);
      const type = (task.action_type || 'OTHER').toUpperCase();
      const bucket = isCadenceStep ? cadenceCounts : otherCounts;
      if (Object.prototype.hasOwnProperty.call(bucket, type)) {
        bucket[type] += 1;
      } else {
        bucket.OTHER += 1;
      }
      bucket.TOTAL += 1;
    });
    return [otherCounts, cadenceCounts];
  } catch {
    return null;
  }
};

export const getPausedTaskCount = async (endDate) => {
  const query = homepageQueries.pausedForDaysTaskCount;
  const queryVars = {
    endDate,
  };

  const { pausedForDaysTaskCount } = await fetchFromGraphql(query, queryVars, 'pausedForDaysTaskCount');
  return pausedForDaysTaskCount;
};

//Fetching counts for Next five days for Journeys and Other tasks
export const fetchNextFiveDays = async (taskOwnerUserId, taskStatuses, includeInactive, dayRange) => {
  if (!taskOwnerUserId) return [];
  const nextFiveJourneyTasks = await getUpcomingTaskCounts(
    taskOwnerUserId,
    taskStatuses,
    includeInactive,
    true,
    dayRange
  );
  const nextFiveOtherTasks = await getUpcomingTaskCounts(
    taskOwnerUserId,
    taskStatuses,
    includeInactive,
    false,
    dayRange
  );

  let merged = [];
  if (nextFiveJourneyTasks && nextFiveOtherTasks) {
    nextFiveJourneyTasks.forEach((journeyDay) => {
      const found = nextFiveOtherTasks.find((taskDay) => taskDay.name === journeyDay.name);
      if (found) {
        const { name, date, ...counts } = found;
        Object.entries(counts).forEach(([key, value]) => {
          journeyDay[key] += value;
        });
      }
      merged.push(journeyDay);
    });

    nextFiveOtherTasks.forEach((taskDay) => {
      const found = merged.find((day) => day.name === taskDay.name);
      if (!found) {
        merged.push(taskDay);
      }
    });

    merged = merged.sort((a, b) => a.date - b.date).slice(0, 5);
  }
  return merged;
};

/**
 * in GQL, there is a query exposed that gives details about a Solicitor Record
 *
 * In our use-case, we don't actually care much about it, just whether any assignments exist.
 * so this returns a boolean indicating if there are any recent assignments.
 *
 * @param {*} user
 * @param {*} org
 * @returns boolean if the user has any recent assignments
 */
export const userHasAssignments = async (user, org) => {
  const userSolicitorContactId = getUserContactIdForOrg(user, org);
  if (!userSolicitorContactId) return false;

  const query = homepageQueries.fetchSolicitorInformation;
  const queryVars = {
    contactId: userSolicitorContactId,
  };

  const { solicitor = {} } = await fetchFromGraphql(query, queryVars, 'solicitor');
  if (!solicitor) return false;

  return solicitor?.assignments_pagination?.count > 0;
};
